// Workstream: growth. Titles, descriptions and link-preview art for every public page. The server
// uses this for <head> tags, preview images and sitemap.xml; the web app uses it for document titles.

export const SITE_NAME = 'CompleteMasti';
export const SITE_TAGLINE = 'A brown-family card game for all generations';

export interface PageMeta {
  path: string;
  /** Short name for the browser tab ("Diwali party games" becomes "Diwali party games · CompleteMasti"). */
  title: string;
  /** Full title for link previews and search results. */
  headline: string;
  description: string;
  /** Link-preview image art: a prompt and the answer that fills its blank. */
  prompt: string;
  answer: string;
  /** List in sitemap.xml. */
  sitemap: boolean;
}

const page = (p: Omit<PageMeta, 'sitemap'> & { sitemap?: boolean }): PageMeta => ({ sitemap: true, ...p });

export const PAGES: PageMeta[] = [
  page({
    path: '/',
    title: '',
    headline: 'CompleteMasti: the desi party card game on everyone’s phones',
    description:
      'A fill-in-the-blank party game for brown families and friends. Aunties, rishtas, filmy dialogues and chai. Free to play on your phones, no app needed.',
    prompt: 'When aunty ji asks when I’m getting married, I change the topic to ___.',
    answer: 'Sharma ji ka beta',
  }),
  page({
    path: '/how-to-play',
    title: 'How to play',
    headline: 'How to play CompleteMasti',
    description: 'Start a room, share the code, and play your funniest card. Your first game takes about 60 seconds to set up.',
    prompt: 'The secret to a happy desi family is ___.',
    answer: 'Muting the family WhatsApp group',
  }),
  page({
    path: '/packs',
    title: 'Card packs',
    headline: 'CompleteMasti card packs',
    description: 'Classic, Filmy Dialogues, Shaadi Season and packs made by players. Add any pack to your game by its code.',
    prompt: 'Mere paas ___ hai.',
    answer: 'A Costco membership',
  }),
  page({
    path: '/packs/new',
    title: 'Make a pack',
    headline: 'Make your own CompleteMasti pack',
    description: 'Write cards full of family inside jokes, share the pack code, and play them in any game. Free, no sign-up.',
    prompt: 'Only our family would find ___ funny.',
    answer: 'Chachu’s one joke from 1998',
    sitemap: false,
  }),
  page({
    path: '/suggest',
    title: 'Suggest a card',
    headline: 'Suggest a card for CompleteMasti',
    description: 'Got a line your family says every single time? Send it over. The good ones go into the official packs.',
    prompt: 'The card this game is missing: ___.',
    answer: 'Yours, obviously',
  }),
  page({
    path: '/games',
    title: 'Party games',
    headline: 'Desi party games for one phone',
    description: 'Dumb Charades, Finish the Dialogue, Mat Bolna and pass-and-play CompleteMasti. One phone, the whole living room, no sign-up.',
    prompt: 'Tonight’s living room game: ___.',
    answer: 'Dumb Charades with Dadi',
  }),
  page({
    path: '/games/one-phone',
    title: 'One-phone mode',
    headline: 'CompleteMasti on one phone',
    description: 'Pass one phone around the room and play CompleteMasti together. No sign-up and no room code.',
    prompt: 'Pass the phone to ___.',
    answer: 'The cousin who never charges theirs',
  }),
  page({
    path: '/games/charades',
    title: 'Dumb Charades',
    headline: 'Dumb Charades: Bollywood films, songs and TV',
    description: 'Film, song and TV titles for Dumb Charades, with difficulty levels, a timer and team scores. Just one phone.',
    prompt: 'Four words, film, and Mamu is pointing at ___.',
    answer: 'The ceiling fan',
  }),
  page({
    path: '/games/dialogue',
    title: 'Finish the Dialogue',
    headline: 'Finish the Dialogue: the filmy quiz',
    description: 'Famous filmy dialogues with a word missing. Teams shout the answer, the quiz master reveals it. One phone, big text.',
    prompt: 'Kitne ___ the?',
    answer: 'Aadmi',
  }),
  page({
    path: '/games/mat-bolna',
    title: 'Mat Bolna',
    headline: 'Mat Bolna: describe it without the forbidden words',
    description: 'Describe the card without saying the five forbidden words. A desi twist on the classic, with a buzzer and team scores.',
    prompt: 'Describe biryani without saying ___.',
    answer: 'Rice, chicken, Hyderabad, dum or aloo',
  }),
  page({
    path: '/shop',
    title: 'Card decks',
    headline: 'CompleteMasti printed card decks',
    description: 'Printed CompleteMasti decks for game nights, weddings and gifts, including a deck made from your own family pack.',
    prompt: 'The perfect gift for the family that has everything: ___.',
    answer: 'A deck full of their own inside jokes',
  }),
  page({
    path: '/events',
    title: 'Event host kit',
    headline: 'CompleteMasti for sangeets, weddings and big desi events',
    description:
      'A custom card pack, TV setup, printed decks and a host script for sangeets, student associations, diaspora groups and office Diwali parties.',
    prompt: 'The sangeet got loud when ___.',
    answer: 'Both sides of the family played one game',
  }),
  page({
    path: '/diwali-party-games',
    title: 'Diwali party games',
    headline: 'Diwali party games for the whole family',
    description:
      'A desi card game for Diwali night that Nani, the cousins and the kids can all play on their phones. Free, quick to set up, and made for mixed crowds.',
    prompt: 'This Diwali, the real fireworks started when ___.',
    answer: 'Mummy found out about the rishta',
  }),
  page({
    path: '/sangeet-games',
    title: 'Sangeet games',
    headline: 'Sangeet games that get both families laughing',
    description:
      'An icebreaker for the sangeet or mehndi that works for both sides of the family, on everyone’s phones or the big screen. Custom wedding packs too.',
    prompt: 'The groom’s side will never forgive ___.',
    answer: 'The DJ playing the same song four times',
  }),
  page({
    path: '/family-zoom-games',
    title: 'Family Zoom games',
    headline: 'Family Zoom games for desi families across time zones',
    description:
      'A party game for video calls with family in India, Pakistan, Bangladesh, the UK and everywhere else. Everyone votes, everyone plays, nobody needs an app.',
    prompt: 'Every family video call starts with ___.',
    answer: 'Papa’s forehead filling the screen',
  }),
  page({
    path: '/desi-party-games',
    title: 'Desi party games',
    headline: 'Desi party games for game night',
    description:
      'A fill-in-the-blank card game about aunties, rishtas, filmy dialogues and chai. Built for desi game nights, dorm parties and family dinners.',
    prompt: 'The one thing every desi party needs: ___.',
    answer: 'An uncle who brought his own speaker',
  }),
  page({
    path: '/eid-party-games',
    title: 'Eid party games',
    headline: 'Eid party games for the whole khandaan',
    description:
      'A family-friendly card game for Eid dinners and Chaand Raat, played on everyone’s phones. Easy for elders, fun for the cousins, free to play.',
    prompt: 'The best part of Eid is ___.',
    answer: 'Counting Eidi before dessert',
  }),
  page({
    path: '/baby-shower-games-desi',
    title: 'Desi baby shower games',
    headline: 'Desi baby shower games for your godh bharai',
    description:
      'A party game for godh bharai and desi baby showers that both the aunties and the friends can play. Make a pack about the parents-to-be.',
    prompt: 'The baby will definitely inherit ___.',
    answer: 'Dadi’s opinions about everything',
  }),
];

const BY_PATH = new Map(PAGES.map((p) => [p.path, p]));

export function pageMeta(path: string): PageMeta | undefined {
  return BY_PATH.get(path === '/' ? path : path.replace(/\/+$/, ''));
}

/** Stable id for a page's preview image: "/games/charades" becomes "games-charades", "/" becomes "home". */
export function pageSlug(path: string): string {
  return path === '/' ? 'home' : path.replace(/^\/+|\/+$/g, '').replace(/\//g, '-');
}

export function pageBySlug(slug: string): PageMeta | undefined {
  return PAGES.find((p) => pageSlug(p.path) === slug);
}

/** Browser tab title, matching useDocumentTitle in the web app. */
export function documentTitle(title: string): string {
  return title ? `${title} · ${SITE_NAME}` : SITE_NAME;
}
